import {
  collection,
  getDocsFromServer,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'

/** Max invites one artist can submit per calendar month (local device month). */
export const MAX_MONTHLY_ARTIST_INVITES = 2

export function startOfCurrentMonthLocal(from: Date = new Date()): Date {
  return new Date(from.getFullYear(), from.getMonth(), 1, 0, 0, 0, 0)
}

export function startOfNextCalendarMonthLocal(from: Date = new Date()): Date {
  return new Date(from.getFullYear(), from.getMonth() + 1, 1, 0, 0, 0, 0)
}

/** Stable key for the current local calendar month, e.g. `2026-05`. */
export function currentCalendarMonthKey(from: Date = new Date()): string {
  const y = from.getFullYear()
  const m = String(from.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/** Milliseconds until the next local calendar month begins (1st at 00:00). */
export function msUntilNextCalendarMonthLocal(from: Date = new Date()): number {
  return Math.max(0, startOfNextCalendarMonthLocal(from).getTime() - from.getTime())
}

/** Whole days until the first day of the next calendar month (local), at least 1. */
export function daysUntilNextCalendarMonthLocal(from: Date = new Date()): number {
  return Math.max(1, Math.ceil(msUntilNextCalendarMonthLocal(from) / 86_400_000))
}

export type ArtistInviteRow = {
  id: string
  inviteInput: string
  createdAt: Date | null
  onboarded: boolean
}

/** Parses Firestore / console edits that might not be a strict JS boolean */
function coerceOnboardedInviteFlag(value: unknown): boolean {
  if (value === true) return true
  if (value === false || value === null || value === undefined) return false
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

export async function fetchArtistInvitesThisMonth(artistId: string): Promise<ArtistInviteRow[]> {
  const monthStart = Timestamp.fromDate(startOfCurrentMonthLocal())
  const monthEnd = Timestamp.fromDate(startOfNextCalendarMonthLocal())
  const q = query(
    collection(db, 'artistInvites'),
    where('artistId', '==', artistId),
    where('createdAt', '>=', monthStart),
    where('createdAt', '<', monthEnd),
    orderBy('createdAt', 'desc'),
    limit(50),
  )
  /** Server read avoids stale IndexedDB cache hiding `onboarded` flips done in Console. */
  const snap = await getDocsFromServer(q)
  return snap.docs.map((d) => {
    const data = d.data()
    const ca = data.createdAt as Timestamp | undefined
    return {
      id: d.id,
      inviteInput: typeof data.inviteInput === 'string' ? data.inviteInput : '',
      createdAt: ca?.toDate?.() ?? null,
      onboarded: coerceOnboardedInviteFlag(data.onboarded),
    }
  })
}

// —— Admin onboarding: match artistInvites.inviteInput ↔ typed Instagram ——

export type AdminFetchedArtistInvite = {
  id: string
  artistId: string
  inviteInput: string
  createdAt: Date | null
  onboarded: boolean
}

/** Recent rows for admin UI (no per-inviting-artist filter). */
export async function fetchRecentArtistInvitesForAdmin(cap = 450): Promise<AdminFetchedArtistInvite[]> {
  const q = query(collection(db, 'artistInvites'), orderBy('createdAt', 'desc'), limit(cap))
  const snap = await getDocsFromServer(q)
  return snap.docs.flatMap((d) => {
    const data = d.data()
    const artistId = typeof data.artistId === 'string' ? data.artistId.trim() : ''
    if (!artistId) return []
    const ca = data.createdAt as Timestamp | undefined
    return [
      {
        id: d.id,
        artistId,
        inviteInput: typeof data.inviteInput === 'string' ? data.inviteInput : '',
        createdAt: ca?.toDate?.() ?? null,
        onboarded: coerceOnboardedInviteFlag(data.onboarded),
      },
    ]
  })
}

export function normalizeInstagramHandleToken(raw: string): string {
  let s = raw.trim().toLowerCase()
  const url = s.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^/?#]+)/i)
  if (url?.[1]) s = url[1]
  s = s.replace(/^@/, '')
  return s.replace(/[^a-z0-9._]/g, '')
}

function handleCandidatesFromInviteText(inviteRaw: string): string[] {
  const out = new Set<string>()
  const full = normalizeInstagramHandleToken(inviteRaw)
  if (full.length >= 2) out.add(full)
  for (const chunk of inviteRaw.split(/[\s,;|]+/)) {
    const n = normalizeInstagramHandleToken(chunk)
    if (n.length >= 2) out.add(n)
  }
  return [...out]
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  const m = a.length
  const n = b.length
  const row = new Uint16Array(n + 1)
  for (let j = 0; j <= n; j++) row[j] = j
  for (let i = 1; i <= m; i++) {
    let prev = row[0]
    row[0] = i
    for (let j = 1; j <= n; j++) {
      const cur = row[j]
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost)
      prev = cur
    }
  }
  return row[n]
}

/**
 * Rough 0–100 score: how strongly `inviteInput` (free text) refers to the same handle as `igTypedRaw`.
 */
export function inviteInputVersusInstagramScore(inviteRaw: string, igTypedRaw: string): number {
  const ig = normalizeInstagramHandleToken(igTypedRaw)
  if (ig.length < 2) return 0
  const candidates = handleCandidatesFromInviteText(inviteRaw)
  if (candidates.length === 0) return 0

  let best = 0
  for (const c of candidates) {
    if (c === ig) {
      best = 100
      break
    }
    if (c.includes(ig) || ig.includes(c)) {
      const longer = Math.max(c.length, ig.length)
      const shorter = Math.min(c.length, ig.length)
      best = Math.max(best, 72 + Math.round((shorter / longer) * 13))
      continue
    }

    const maxLen = Math.max(c.length, ig.length)
    if (maxLen > 28) continue
    const d = levenshtein(c, ig)
    const ratio = maxLen ? 1 - d / maxLen : 0
    if (d <= 1 && maxLen <= 14) {
      best = Math.max(best, 68)
    } else if (d <= 2 && maxLen <= 12) {
      best = Math.max(best, 62)
    } else if (ratio >= 0.75) {
      best = Math.max(best, Math.round(45 + ratio * 30))
    }
  }
  return best
}

export type InviterSuggestionFromInvite = {
  artistId: string
  score: number
  inviteInput: string
}

export function rankInviterSuggestionsFromInvites(
  invites: readonly { artistId: string; inviteInput: string }[],
  igTypedRaw: string,
  minScore = 52,
): InviterSuggestionFromInvite[] {
  const ig = igTypedRaw.trim()
  if (normalizeInstagramHandleToken(ig).length < 2) return []

  const byArtist = new Map<string, { score: number; inviteInput: string }>()
  for (const row of invites) {
    if (typeof row.artistId !== 'string' || !row.artistId) continue
    const input = typeof row.inviteInput === 'string' ? row.inviteInput : ''
    const score = inviteInputVersusInstagramScore(input, ig)
    if (score < minScore) continue
    const prev = byArtist.get(row.artistId)
    const trimmed = input.trim().slice(0, 160)
    if (!prev || score > prev.score) {
      byArtist.set(row.artistId, { score, inviteInput: trimmed })
    }
  }

  return [...byArtist.entries()]
    .map(([artistId, v]) => ({ artistId, score: v.score, inviteInput: v.inviteInput }))
    .sort((a, b) => b.score - a.score || a.inviteInput.localeCompare(b.inviteInput))
}
